import { useState, useEffect } from 'react';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { useAuth } from '@/hooks/use-auth';
import { PaymentRecord, MembershipSummary } from '@/types/payment';

function mapFromDb(row: any): PaymentRecord {
  return {
    id: row.id,
    memberId: row.member_id,
    memberName: row.member_name || '',
    amount: row.amount,
    currency: row.currency || 'USD',
    paymentDate: row.payment_date || '',
    dueDate: row.due_date,
    status: row.status,
    paymentMethod: row.payment_method || '',
    description: row.description,
    adminId: row.admin_id || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapToDb(payment: Partial<PaymentRecord>) {
  const mapped: any = {};
  if (payment.memberId !== undefined) mapped.member_id = payment.memberId;
  if (payment.memberName !== undefined) mapped.member_name = payment.memberName;
  if (payment.amount !== undefined) mapped.amount = payment.amount;
  if (payment.currency !== undefined) mapped.currency = payment.currency;
  if (payment.paymentDate !== undefined) mapped.payment_date = payment.paymentDate || null;
  if (payment.dueDate !== undefined) mapped.due_date = payment.dueDate;
  if (payment.status !== undefined) mapped.status = payment.status;
  if (payment.paymentMethod !== undefined) mapped.payment_method = payment.paymentMethod;
  if (payment.description !== undefined) mapped.description = payment.description;
  if (payment.adminId !== undefined) mapped.admin_id = payment.adminId;
  if (payment.createdAt !== undefined) mapped.created_at = payment.createdAt;
  if (payment.updatedAt !== undefined) mapped.updated_at = payment.updatedAt;
  return mapped;
}

export function usePayments() {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const supabase = useSupabase();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;

    const load = async () => {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .order('due_date', { ascending: true });
      if (error) {
        console.error('Error loading payments:', error);
        return;
      }
      setPayments((data || []).map(mapFromDb));
    };
    load();
  }, [isAuthenticated, supabase]);

  const addPayment = async (
    payment: Omit<PaymentRecord, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    const timestamp = new Date().toISOString();
    const toInsert = mapToDb({ ...payment, createdAt: timestamp, updatedAt: timestamp });
    const { data, error } = await supabase
      .from('payments')
      .insert(toInsert)
      .select()
      .single();
    if (error) {
      console.error('Error adding payment:', error);
      throw error;
    }
    const mapped = mapFromDb(data);
    setPayments(prev => [...prev, mapped]);
    return mapped;
  };

  const updatePayment = async (id: string, updates: Partial<PaymentRecord>) => {
    const toUpdate = mapToDb({ ...updates, updatedAt: new Date().toISOString() });
    const { data, error } = await supabase
      .from('payments')
      .update(toUpdate)
      .eq('id', id)
      .select()
      .single();
    if (error) {
      console.error('Error updating payment:', error);
      throw error;
    }
    const mapped = mapFromDb(data);
    setPayments(prev => prev.map(p => (p.id === id ? mapped : p)));
  };

  const markPaymentAsPaid = async (
    id: string,
    paymentDate: string,
    paymentMethod: string
  ) => {
    await updatePayment(id, {
      status: 'paid',
      paymentDate,
      paymentMethod,
    });
  };

  const getMembershipSummaries = (): MembershipSummary[] => {
    const memberMap = new Map<string, MembershipSummary>();

    payments.forEach(payment => {
      if (!memberMap.has(payment.memberId)) {
        memberMap.set(payment.memberId, {
          memberId: payment.memberId,
          memberName: payment.memberName,
          totalPaid: 0,
          totalDue: 0,
          status: 'current',
        });
      }

      const summary = memberMap.get(payment.memberId)!;

      if (payment.status === 'paid') {
        summary.totalPaid += payment.amount;
        if (!summary.lastPaymentDate || payment.paymentDate > summary.lastPaymentDate) {
          summary.lastPaymentDate = payment.paymentDate;
        }
      } else {
        summary.totalDue += payment.amount;
        if (payment.status === 'overdue') {
          summary.status = 'overdue';
        } else if (summary.status !== 'overdue' && payment.status === 'pending') {
          summary.status = 'pending';
        }
        if (!summary.upcomingDueDate || payment.dueDate < summary.upcomingDueDate) {
          summary.upcomingDueDate = payment.dueDate;
        }
      }
    });

    return Array.from(memberMap.values());
  };

  const getPaymentStats = () => {
    const totalPayments = payments.length;
    const paidPayments = payments.filter(p => p.status === 'paid').length;
    const pendingPayments = payments.filter(p => p.status === 'pending').length;
    const overduePayments = payments.filter(p => p.status === 'overdue').length;
    const totalRevenue = payments
      .filter(p => p.status === 'paid')
      .reduce((sum, p) => sum + p.amount, 0);
    const pendingRevenue = payments
      .filter(p => p.status !== 'paid')
      .reduce((sum, p) => sum + p.amount, 0);

    return {
      totalPayments,
      paidPayments,
      pendingPayments,
      overduePayments,
      totalRevenue,
      pendingRevenue,
      paymentRate: totalPayments > 0 ? Math.round((paidPayments / totalPayments) * 100) : 0,
    };
  };

  const addPaymentsBulk = async (
    newItems: Omit<PaymentRecord, 'id' | 'createdAt' | 'updatedAt'>[]
  ) => {
    const timestamp = new Date().toISOString();
    const records = newItems.map(item =>
      mapToDb({ ...item, createdAt: timestamp, updatedAt: timestamp })
    );
    const { data, error } = await supabase
      .from('payments')
      .insert(records)
      .select();
    if (error) {
      console.error('Error adding payments in bulk:', error);
      throw error;
    }
    const mapped = (data || []).map(mapFromDb);
    setPayments(prev => [...prev, ...mapped]);
    return mapped;
  };

  const importPayments = async (incoming: Partial<PaymentRecord>[]) => {
    const timestamp = new Date().toISOString();
    const sanitized = incoming.map((p, idx) => ({
      id: p.id || `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`,
      memberId: p.memberId || `member-${Date.now()}-${idx}`,
      memberName: p.memberName || 'Unknown',
      amount: typeof p.amount === 'number' ? p.amount : Number(p.amount || 0),
      currency: p.currency || 'USD',
      paymentDate: p.paymentDate || null,
      dueDate: p.dueDate || new Date().toISOString().split('T')[0],
      status: (p.status as any) || 'pending',
      paymentMethod: p.paymentMethod || '',
      description: p.description || 'Imported payment',
      adminId: p.adminId,
      createdAt: p.createdAt || timestamp,
      updatedAt: timestamp,
    }));
    const records = sanitized.map(mapToDb);
    const { data, error } = await supabase
      .from('payments')
      .insert(records)
      .select();
    if (error) {
      console.error('Error importing payments:', error);
      throw error;
    }
    const mapped = (data || []).map(mapFromDb);
    setPayments(prev => [...prev, ...mapped]);
    return mapped;
  };

  const autoUpdateOverdues = async () => {
    const today = new Date().toISOString().split('T')[0];
    const toUpdate = payments.filter(
      p => p.status !== 'paid' && p.dueDate < today && p.status !== 'overdue'
    );
    if (!toUpdate.length) return false;
    const ids = toUpdate.map(p => p.id);
    const { error } = await supabase
      .from('payments')
      .update({ status: 'overdue', updated_at: new Date().toISOString() })
      .in('id', ids);
    if (error) {
      console.error('Error auto-updating overdues:', error);
      return false;
    }
    setPayments(prev =>
      prev.map(p => (ids.includes(p.id) ? { ...p, status: 'overdue' } : p))
    );
    return true;
  };

  return {
    payments,
    addPayment,
    updatePayment,
    markPaymentAsPaid,
    getMembershipSummaries,
    getPaymentStats,
    addPaymentsBulk,
    importPayments,
    autoUpdateOverdues,
  };
}
